import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'
import { formatNumberEnglish, parseServiceFeePercentage, parseServicePrice } from '../../utils'
import OrderItemRow from './OrderItemRow'
import CurrencyDisplay from '../shared/CurrencyDisplay'
import { parseOrderPromotion } from '../../features/offers/orderPromotion'

interface OrderConfirmationContentProps {
  order: any
  isRTL: boolean
}

const OrderConfirmationContent: React.FC<OrderConfirmationContentProps> = ({
  order,
  isRTL
}) => {
  const { t } = useTranslation()
  // Extract order items - try different possible structures
  const items = order?.items || order?.meals || order?.order_items || order?.cartItems || []
  
  // Use data directly from API - no local calculations
  const itemsTotal = parseFloat(String(order?.price_before_discount || order?.sub_total || order?.subtotal || '0'))
  const taxAmount = parseFloat(String(order?.tax_value || order?.tax_amount || order?.tax || '0'))
  const totalAmount = parseFloat(String(order?.total_price || order?.total_amount || '0'))
  const taxPercentage = parseFloat(String(order?.tax_percentage || '15'))
  const discount = typeof order?.discount === 'string' ? parseFloat(order.discount) : (order?.discount || 0)
  const deliveryPrice = parseFloat(String(order?.delivery_price || order?.deliveryPrice || '0'))
  // Extract table price from multiple possible locations in API response
  const tablePrice = parseFloat(String(
    order?.table?.table_price || 
    order?.table_price || 
    order?.tablePrice || 
    '0'
  ))
  
  // Use total_price from API directly - it already includes everything
  const finalTotal = totalAmount

  return (
    <>
      {/* Order Items - This will be scrollable */}
      <div className="w-full flex flex-col justify-start items-start">
        {items.length === 0 ? (
          <div className="w-full text-center text-gray-500 text-base font-['IBM_Plex_Sans_Arabic'] py-4">
            {t('order.no_items')}
          </div>
        ) : (
          items.map((item: any, index: number) => (
            <OrderItemRow key={item.id || item.meal_id || index} item={item} isRTL={isRTL} />
          ))
        )}
      </div>
    </>
  )
}

// Export summary section separately
export const OrderConfirmationSummary: React.FC<OrderConfirmationContentProps> = ({
  order,
  isRTL
}) => {
  const { t, i18n } = useTranslation()
  const settingsServiceFeePercentage = useSelector((state: RootState) => state.settings.orderServiceFeePercentage)
  // Extract order items - try different possible structures
  const items = order?.items || order?.meals || order?.order_items || order?.cartItems || []
  
  // Use data directly from API - no local calculations
  const itemsTotal = parseFloat(String(order?.price_before_discount || order?.sub_total || order?.subtotal || '0'))
  const taxAmount = parseFloat(String(order?.tax_value || order?.tax_amount || order?.tax || '0'))
  const totalAmount = parseFloat(String(order?.total_price || order?.total_amount || '0'))
  const taxPercentage = parseFloat(String(order?.tax_percentage || '15'))
  const discount = typeof order?.discount === 'string' ? parseFloat(order.discount) : (order?.discount || 0)
  const deliveryPrice = parseFloat(String(order?.delivery_price || order?.deliveryPrice || '0'))
  // Extract table price from multiple possible locations in API response
  // Check order.table.table_price first, then fallback to other locations
  const tablePriceValue = order?.table?.table_price !== undefined && order?.table?.table_price !== null
    ? order.table.table_price
    : (order?.table_price !== undefined && order?.table_price !== null
      ? order.table_price
      : (order?.tablePrice !== undefined && order?.tablePrice !== null
        ? order.tablePrice
        : '0'))
  const tablePrice = parseFloat(String(tablePriceValue))
  const servicePrice = parseServicePrice(order)
  const serviceFeePercentage = parseServiceFeePercentage(order) ?? settingsServiceFeePercentage
  
  // Use total_price from API directly - it already includes everything
  const finalTotal = totalAmount
  const discountValue = discount

  // Get order type_id to determine if we should show table_price and delivery_price
  const orderTypeId = order?.type_id || order?.type?.id
  const orderTypeIdNum = orderTypeId !== undefined && orderTypeId !== null
    ? (typeof orderTypeId === 'string' ? parseInt(orderTypeId) : Number(orderTypeId))
    : null
  // Show table_price only if type_id is 3 (dine_in/محلي), even if zero
  const shouldShowTablePrice = orderTypeIdNum === 3
  // Show delivery_price only if type_id is 1 (delivery/تواصيل), even if zero
  const shouldShowDeliveryPrice = orderTypeIdNum === 1

  // Memoize translations to ensure they update when language changes
  const translations = useMemo(() => ({
    totalItems: t('order.total_items'),
    discount: t('order.discount'),
    tax: t('order.tax'),
    tablePrice: t('order.table_price'),
    deliveryPrice: t('order.delivery_price'),
    servicePrice: t('order.service_price'),
    total: t('order.total'),
    currency: t('common.currency'),
  }), [t, i18n.language])

  const orderPromotion = useMemo(
    () => parseOrderPromotion(order?.promotion),
    [order?.promotion],
  )

  const promotionDiscount = orderPromotion?.discount ?? 0
  const promotionCashback = orderPromotion?.cashback ?? 0
  const promotionSetItem =
    orderPromotion?.set_item_price_savings ??
    orderPromotion?.setItemPriceSavings ??
    0

  return (
    <>
      {/* Divider Line */}
      <div className="w-full h-[1px] bg-gray-300 my-"></div>

      {/* Summary */}
      <div className="w-full flex flex-col justify-start items-start gap-2 py-4">
        {/* Subtotal */}
        <div className="w-full flex justify-between items-center">
          <div className=" text-gray-600 text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
            {translations.totalItems}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-600 text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
              {formatNumberEnglish(itemsTotal, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <CurrencyDisplay className="w-4 h-4" />
          </div>
        </div>

        {/* Tax */}
        <div className="w-full flex justify-between items-center">
          <div className="text-right text-gray-600 text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
            {translations.tax} ({Math.round(taxPercentage)}%)
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-600 text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
              {formatNumberEnglish(taxAmount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <CurrencyDisplay className="w-4 h-4" />
          </div>
        </div>

        <div className="w-full flex justify-between items-center">
          <div className="text-right text-gray-600 text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
            {serviceFeePercentage != null
              ? t('home.service_fee_with_percentage', { percentage: serviceFeePercentage })
              : translations.servicePrice}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-600 text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
              {formatNumberEnglish(servicePrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <CurrencyDisplay className="w-4 h-4" />
          </div>
        </div>

        {/* Promotion discount */}
        {promotionDiscount > 0 && (
          <div className="w-full flex justify-between items-center">
            <div className="text-right text-green-600 text-sm font-medium font-['IBM_Plex_Sans_Arabic']">
              {t('offers.benefitDiscount')}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-green-600 text-sm font-medium font-['IBM_Plex_Sans_Arabic']">
                -{formatNumberEnglish(promotionDiscount, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <CurrencyDisplay className="w-4 h-4" />
            </div>
          </div>
        )}

        {/* Coupon discount fallback */}
        {promotionDiscount <= 0 &&
          (discountValue > 0 || (order?.coboneCode && order.coboneCode.length > 0)) && (
          <div className="w-full flex justify-between items-center">
            <div className="text-right text-green-600 text-sm font-medium font-['IBM_Plex_Sans_Arabic']">
              {translations.discount}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-green-600 text-sm font-medium font-['IBM_Plex_Sans_Arabic']">
                -{formatNumberEnglish(discountValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <CurrencyDisplay className="w-4 h-4" />
            </div>
          </div>
        )}

        {/* Cashback — display only */}
        {promotionCashback > 0 && (
          <div className="w-full flex justify-between items-center">
            <div className="text-right text-green-600 text-sm font-medium font-['IBM_Plex_Sans_Arabic']">
              {t('offers.benefitCashback')}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-green-600 text-sm font-medium font-['IBM_Plex_Sans_Arabic']">
                {formatNumberEnglish(promotionCashback, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <CurrencyDisplay className="w-4 h-4" />
            </div>
          </div>
        )}

        {/* Set item product discount — subtracted from total */}
        {promotionSetItem > 0 && (
          <div className="w-full flex justify-between items-center">
            <div className="text-right text-green-600 text-sm font-medium font-['IBM_Plex_Sans_Arabic']">
              {t('offers.benefitSetItemDiscount')}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-green-600 text-sm font-medium font-['IBM_Plex_Sans_Arabic']">
                -{formatNumberEnglish(promotionSetItem, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <CurrencyDisplay className="w-4 h-4" />
            </div>
          </div>
        )}

        {/* Table Price - Show only if type_id is 3 (محلي), even if zero */}
        {shouldShowTablePrice && (
          <>
            <div className="w-full flex justify-between items-center">
              <div className="text-right text-gray-600 text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
                {translations.tablePrice}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-600 text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
                  {formatNumberEnglish(tablePrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <CurrencyDisplay className="w-4 h-4" />
              </div>
            </div>
          </>
        )}

        {/* Delivery Price - Show only if type_id is 1 (تواصيل), even if zero */}
        {shouldShowDeliveryPrice && (
          <>
            <div className="w-full flex justify-between items-center">
              <div className="text-right text-gray-600 text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
                {translations.deliveryPrice}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-600 text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
                  {formatNumberEnglish(deliveryPrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <CurrencyDisplay className="w-4 h-4" />
              </div>
            </div>
          </>
        )}

        {/* Divider Line */}
        <div className="w-full h-[1px] bg-gray-300 my-2"></div>

        {/* Final Total */}
        <div className="w-full flex justify-between items-center">
          <div className="text-right text-black text-base font-semibold font-['IBM_Plex_Sans_Arabic']">
            {translations.total}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-black text-base font-semibold font-['IBM_Plex_Sans_Arabic']">
              {formatNumberEnglish(finalTotal, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <CurrencyDisplay className="w-4 h-4" />
          </div>
        </div>
      </div>
    </>
  )
}

export default OrderConfirmationContent

